import { MutableRefObject, useEffect, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import { useAppDispatch } from "app/hooks";
import { type TextTemplate } from "services/types/text-templates.type";

import { getUniqueId } from "../word-processor-field.utils";
import {
  clearDataParams,
  selectDataParamsById,
  setDataParams
} from "../word-processor.slice";

interface UseWordProcessorIdParams {
  fieldIdRef: MutableRefObject<string | undefined>;
  name: string;
  featuresGroup: string;
  optionsUpdated: number;
  mergeTagsPatientId?: number;
  mergeTagsSaleId?: number;
  defaultFontSize?: string | null | number | undefined;
  contextSaleId?: number;
  textTemplates: TextTemplate[];
  onIdGenerated?: (id: string) => void;
  allowImageAutoUploadFeature?: boolean;
}

export function useWordProcessorId({
  fieldIdRef,
  name,
  featuresGroup,
  optionsUpdated,
  mergeTagsPatientId,
  mergeTagsSaleId,
  defaultFontSize,
  contextSaleId,
  textTemplates,
  onIdGenerated,
  allowImageAutoUploadFeature
}: UseWordProcessorIdParams) {
  const dispatch = useAppDispatch();
  const params = useParams();

  // Track previous ID for cleanup
  const previousIdRef = useRef<string | undefined>();

  // Get existing dataParams before ID changes
  const dataParams = useSelector((state: any) =>
    selectDataParamsById(state, String(fieldIdRef.current || ""))
  );

  // When dependencies change, generate a new ID to reinitialize TinyCloudMCE properly
  const id = useMemo(
    () => {
      const newId = getUniqueId();
      fieldIdRef.current = newId;
      return newId;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      name,
      featuresGroup,
      optionsUpdated,
      mergeTagsPatientId,
      mergeTagsSaleId,
      defaultFontSize,
      contextSaleId,
      textTemplates,
      allowImageAutoUploadFeature
    ]
  );

  // Handle clearing old params and setting new ID in Redux
  useEffect(() => {
    const previousId = previousIdRef.current;
    const previousData = { ...dataParams };

    // Clear old dataParams if there was a previous ID and it's different
    if (previousId && previousId !== id) {
      dispatch(clearDataParams(previousId));
    }

    // Set new dataParams with the new ID
    dispatch(setDataParams({ ...previousData, id }));

    // Update previous ID ref for next render
    previousIdRef.current = id;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, dispatch]);

  // Notify parent when ID is generated
  useEffect(() => {
    onIdGenerated?.(id);
  }, [id, onIdGenerated]);

  // Calculate patientId from URL params
  const patientId = useMemo(
    () =>
      mergeTagsPatientId === -1
        ? undefined
        : mergeTagsPatientId ||
          Number(
            (params.patientId || String(params["*"] ?? "").split("/")[0]) ?? ""
          ),
    [mergeTagsPatientId, params]
  );

  // Update dataParams with patientId and saleId
  useEffect(() => {
    dispatch(
      setDataParams({
        id,
        patientId,
        saleId: mergeTagsSaleId
      })
    );
  }, [dispatch, id, patientId, mergeTagsSaleId]);

  return id;
}
